import React, { useState } from 'react'
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material'
import { useLocation } from 'react-router-dom'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'

const ConfirmRegistration: React.FC = () => {
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<'IDLE' | 'SUCCESS' | 'ERROR'>('IDLE')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const query = new URLSearchParams(location.search)
  const registrationId = query.get('registrationId')

  const basePath = Env.basePath
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)

  const handleConfirm = () => {
    if (!registrationId) {
      setStatus('ERROR')
      setErrorMessage('Keine Registrierungs-ID gefunden.')
      return
    }

    setLoading(true)
    fetch(`${basePath}/api/public/events/confirm-registration?registrationId=${registrationId}`, {
      method: 'POST',
      headers: requestHeaders,
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.errorMessage || data.error || 'Bestätigung fehlgeschlagen')
        }
        setStatus('SUCCESS')
      })
      .catch((err) => {
        console.error('Error during confirmation:', err)
        setStatus('ERROR')
        setErrorMessage(err.message || 'Bei der Bestätigung ist ein Fehler aufgetreten.')
      })
      .finally(() => setLoading(false))
  }

  return (
    <Container maxWidth='sm' sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography variant='h4' gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Anmeldung bestätigen
        </Typography>

        {status === 'IDLE' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant='body1' sx={{ mb: 4 }}>
              Vielen Dank für Deine Anmeldung! Bitte klicke auf den Button unten, um Deine E-Mail-Adresse und Deine Teilnahme zu bestätigen.
            </Typography>
            <Button
              variant='contained'
              color='primary'
              size='large'
              onClick={handleConfirm}
              disabled={loading || !registrationId}
              sx={{ minWidth: 200, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color='inherit' /> : 'Jetzt bestätigen'}
            </Button>
            {!registrationId && (
              <Typography color='error' sx={{ mt: 2 }}>
                Ungültiger Bestätigungslink.
              </Typography>
            )}
          </Box>
        )}

        {status === 'SUCCESS' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant='h5' gutterBottom sx={{ color: 'success.main' }}>
              Bestätigung erfolgreich!
            </Typography>
            <Typography variant='body1' sx={{ mb: 4 }}>
              Deine Anmeldung wurde erfolgreich bestätigt. Wir freuen uns auf Dich beim Markencamp!
            </Typography>
            <Button
              variant='outlined'
              color='primary'
              href='https://www.markencamp.com'
              sx={{ minWidth: 200 }}
            >
              Zurück zur Webseite
            </Button>
          </Box>
        )}

        {status === 'ERROR' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant='h5' gutterBottom sx={{ color: 'error.main' }}>
              Hoppla!
            </Typography>
            <Typography variant='body1' sx={{ mb: 4 }}>
              {errorMessage}
            </Typography>
            <Button
              variant='contained'
              color='primary'
              onClick={() => setStatus('IDLE')}
              sx={{ minWidth: 200 }}
            >
              Erneut versuchen
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  )
}

export default ConfirmRegistration
